import { FC, Fragment, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen } from 'lucide-react';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { slotService } from 'v2/api/slotService';
import { SlotModel } from 'v2/types/SlotModel';
import { formatNumber } from 'v2/utils';

interface SlotTableOrderColumnProps {
  slot: SlotModel;
  onRevalidate: VoidFunction;
}

export const SlotTableOrderColumn: FC<SlotTableOrderColumnProps> = ({ slot, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const formik = useFormik({
    initialValues: {
      order: slot.order,
    },
    onSubmit: (values) => {
      trigger(values.order);
    },
    enableReinitialize: true,
  });

  const { trigger, isMutating } = useSWRMutation(['updateSlotOrder', slot.id], slotService.updateSlotOrder, {
    onSuccess: () => {
      onRevalidate();
      handleClose();
    },
  });

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} endIcon={<SquarePen width={14} height={14} />}>
        {formatNumber(slot.order)}
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {slot.title} Order</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Order"
              type="number"
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Edit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
